import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  type Programmvorgabe,
  programmvorgabe as holeVorgabe,
  programmvorgabeSetzen,
  trainerqualifikationen as holeQualifikationen,
} from '../api/produkte';
import { Meldung } from '../komponenten/Meldung';
import { Abschnitt } from '../komponenten/Akte';

/**
 * Programmvorgaben je Trainingsprodukt (PRD-23 i. d. F. ÄA-14, SYS-10).
 *
 * ============================================================================
 * WARUM DIESE MASKE ENTSTANDEN IST
 * ============================================================================
 *
 * PRD-23: „Alle diese Werte sind Konfiguration nach Tabelle 10 und werden von
 * der Administration selbst gepflegt; **das System wird ohne fest eingebaute
 * Programme ausgeliefert** (E-74)."
 *
 * Bis zum 09.09.2026 war das zur Hälfte wahr: Die Tabelle gibt es seit
 * Migration 0094, gefüllt hat sie ein Seed, und weder Endpunkt noch Maske
 * konnten sie anfassen. Damit war das Programm doch eingebaut — nur eben von
 * uns statt vom Auftraggeber. `trainerqualifikation` stand in 36 Zeilen und
 * war überall leer.
 *
 * ============================================================================
 * DIE TEILNEHMERZAHL IST DER BETREUUNGSSCHLÜSSEL (AeA-34)
 * ============================================================================
 *
 * „Teilnehmer je Trainer" gibt es nicht mehr (ÄA-14). Ein Termin trägt eine
 * Gruppe (E-38) und einen betreuenden Trainer (TAG-71); beide Zahlen konnten
 * gar nicht auseinanderfallen, und in allen 36 gepflegten Vorgaben waren sie
 * identisch. Zwei Felder für dieselbe Zahl sind kein Komfort, sondern eine
 * Fehlerquelle.
 *
 * ============================================================================
 * DIE QUELLE IST EIN HINWEIS
 * ============================================================================
 *
 * E-63 sagt es ausdrücklich: Auf welcher externen Vorgabe die Werte beruhen —
 * ADAC, DVR, eine Berufsgenossenschaft — ist „ein Hinweis für die planenden
 * Mitarbeiter und wird vom System nicht ausgewertet oder geprüft". Das Feld
 * steht deshalb hier und wirkt nirgends.
 */
export function Programmvorgaben({
  artikel,
  darfKonfigurieren,
}: {
  readonly artikel: string;
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [formular, setzeFormular] = useState<Formular | null>(null);
  const [qualifikationen, setzeQualifikationen] = useState<
    readonly { readonly schluessel: string; readonly bezeichnung: string }[]
  >([]);
  const [gepflegt, setzeGepflegt] = useState(false);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [meldung, setzeMeldung] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);

  const laden = useCallback(async (): Promise<void> => {
    setzeLaedt(true);
    try {
      const [antwort, quals] = await Promise.all([
        holeVorgabe(artikel),
        holeQualifikationen(),
      ]);
      setzeGepflegt(antwort.programmvorgabe !== null);
      setzeFormular(
        antwort.programmvorgabe === null ? leer() : ausVorgabe(antwort.programmvorgabe),
      );
      setzeQualifikationen(quals);
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, [artikel]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function speichern(ereignis: React.FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (formular === null) return;
    setzeLaeuft(true);
    setzeFehler(undefined);
    setzeMeldung('');
    try {
      await programmvorgabeSetzen(artikel, {
        mindestteilnehmer: Number(formular.mindestteilnehmer),
        teilnehmerzahl: Number(formular.teilnehmerzahl),
        trainingsdauerStunden: leerAlsNull(formular.trainingsdauerStunden),
        trainerqualifikation: leerAlsNull(formular.trainerqualifikation),
        vorgabequelle: leerAlsNull(formular.vorgabequelle),
        vorgabefassung: leerAlsNull(formular.vorgabefassung),
      });
      setzeMeldung('Programmvorgaben gespeichert.');
      await laden();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  if (laedt || formular === null) {
    return <p role="status">Wird geladen …</p>;
  }

  const setze = (teil: Partial<Formular>): void => {
    setzeFormular({ ...formular, ...teil });
  };

  /* ÄA-41: ein aufklappbarer Abschnitt im Reiter „Programm" der Artikelakte. */
  return (
    // PRD-23
    <Abschnitt titel="Programmvorgaben">

      {fehler !== undefined && <Meldung fehler={fehler} />}
      {meldung !== '' && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {!gepflegt && (
        <p className="hinweiszeile">
          Für dieses Produkt sind noch keine Vorgaben gepflegt. Sie belegen Termin und
          Tagesplan vor — ohne sie fehlt der Planung die Teilnehmerzahl, und die
          Qualifikationsprüfung des Trainers hat nichts zu vergleichen.
        </p>
      )}

      <form onSubmit={(e) => void speichern(e)}>
        {/*
          AeA-34 (19.09.2026): Aus Mindest- und Höchstgruppengröße wurden
          Mindestteilnehmerzahl und Teilnehmerzahl; die Standardzahl der
          Gruppen ist entfallen — jeder Termin steht für sich.
        */}
        <fieldset>
          <legend>Teilnehmer</legend>
          <div className="feldreihe feldreihe--halb">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="pvMin">
                Mindestteilnehmerzahl
              </label>
              <input
                id="pvMin"
                type="number"
                min={1}
                className="feld__eingabe"
                value={formular.mindestteilnehmer}
                disabled={!darfKonfigurieren}
                onChange={(e) => setze({ mindestteilnehmer: e.target.value })}
              />
              {/* BUC-09 */}
              <p className="feld__hilfe">Darunter kann ein Termin abgesagt werden.</p>
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="pvMax">
                Teilnehmerzahl
              </label>
              <input
                id="pvMax"
                type="number"
                min={1}
                className="feld__eingabe"
                value={formular.teilnehmerzahl}
                disabled={!darfKonfigurieren}
                onChange={(e) => setze({ teilnehmerzahl: e.target.value })}
              />
              {/* TAG-15, AeA-30 */}
              <p className="feld__hilfe">
                Zugleich der <strong>Betreuungsschlüssel</strong>: Ein Termin trägt eine
                Gruppe und einen betreuenden Trainer. Die Zahl steht auf der Produktseite
                des Portals.
              </p>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Trainer</legend>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pvQualifikation">
              Erforderliche Trainerqualifikation
            </label>
            {/*
              EINE AUSWAHL UND KEIN FREIES FELD MEHR (Festlegung vom
              09.09.2026, umgesetzt in 0158).

              Bis dahin stand hier ein Eingabefeld mit Vorschlagsliste, und
              der Schlüssel war an beiden Enden Freitext. Ein Tippfehler hieß
              stillschweigend „kein Trainer qualifiziert" (RES-54, TAG-14) —
              und genau so ist in dieser Spalte bei 21 von 36 Produkten eine
              Teilnahmevoraussetzung gelandet.

              Jetzt gibt es vier gepflegte Werte. Die Datenbank hält es
              zusätzlich fest; hier steht es, damit gar nichts anderes
              eingegeben werden kann.
            */}
            <select
              id="pvQualifikation"
              className="feld__eingabe"
              value={formular.trainerqualifikation}
              disabled={!darfKonfigurieren}
              onChange={(e) => setze({ trainerqualifikation: e.target.value })}
            >
              {/* Leer ist eine Aussage: Dieses Training verlangt keine bestimmte. */}
              <option value="">— keine bestimmte Qualifikation —</option>
              {qualifikationen.map((q) => (
                <option key={q.schluessel} value={q.schluessel}>
                  {q.bezeichnung}
                </option>
              ))}
            </select>
            {/* RES-02 */}
            <p className="feld__hilfe">
              Ein Trainer wird diesem Training nur zugeordnet, wenn er diese Qualifikation
              mit gültigem Nachweis trägt. Ohne Angabe ist jeder Trainer
              zuteilbar.
              {qualifikationen.length === 0 && (
                <>
                  {' '}
                  <strong>Es ist noch keine Qualifikation eingerichtet.</strong>
                </>
              )}
            </p>
          </div>
        </fieldset>

        <fieldset>
          <legend>Dauer</legend>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pvStunden">
              Trainingsdauer in Stunden
            </label>
            <input
              id="pvStunden"
              className="feld__eingabe feld__eingabe--schmal"
              inputMode="decimal"
              value={formular.trainingsdauerStunden}
              disabled={!darfKonfigurieren}
              onChange={(e) => setze({ trainingsdauerStunden: e.target.value })}
              placeholder="4.50"
            />
            {/* AeA-30, AeA-34 */}
            <p className="feld__hilfe">
              Zeitstunden ohne Pausen, Punkt als Dezimaltrennzeichen. Die Angabe für den
              Kunden: Das Portal zeigt sie auf der Produktseite. Die Trainingsplanung
              bemisst die Dauer an den Bausteinen der Trainingsdefinition.
            </p>
          </div>
        </fieldset>

        <fieldset>
          <legend>Herkunft der Werte</legend>
          <div className="feldreihe feldreihe--halb">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="pvQuelle">
                Externe Vorgabe
              </label>
              <input
                id="pvQuelle"
                className="feld__eingabe"
                value={formular.vorgabequelle}
                disabled={!darfKonfigurieren}
                onChange={(e) => setze({ vorgabequelle: e.target.value })}
                placeholder="ADAC"
              />
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="pvFassung">
                Fassung
              </label>
              <input
                id="pvFassung"
                className="feld__eingabe"
                value={formular.vorgabefassung}
                disabled={!darfKonfigurieren}
                onChange={(e) => setze({ vorgabefassung: e.target.value })}
              />
            </div>
          </div>
          {/* E-63 */}
          <p className="feld__hilfe">
            Ein Hinweis für die planenden Mitarbeiter. Das System wertet ihn nicht aus und
            prüft ihn nicht.
          </p>
        </fieldset>

        {darfKonfigurieren && (
          <div className="knopfreihe">
            <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
              Programmvorgaben speichern
            </button>
          </div>
        )}
      </form>
    </Abschnitt>
  );
}

/** Das Formular führt Text — ein leeres Feld ist kein 0. */
type Formular = {
  mindestteilnehmer: string;
  teilnehmerzahl: string;
  trainingsdauerStunden: string;
  trainerqualifikation: string;
  vorgabequelle: string;
  vorgabefassung: string;
};

/**
 * Die Anfangswerte eines Produkts ohne Vorgaben.
 *
 * 1 bis 12 Teilnehmer: die kleinste Angabe, die die Bedingungen
 * der Tabelle erfüllt. Sie sind ausdrücklich **kein Vorschlag**, wie ein
 * Training aussieht — das entscheidet der Auftraggeber (E-74).
 */
function leer(): Formular {
  return {
    mindestteilnehmer: '1',
    teilnehmerzahl: '12',
    trainingsdauerStunden: '',
    trainerqualifikation: '',
    vorgabequelle: '',
    vorgabefassung: '',
  };
}

function ausVorgabe(v: Programmvorgabe): Formular {
  return {
    mindestteilnehmer: String(v.mindestteilnehmer),
    teilnehmerzahl: String(v.teilnehmerzahl),
    trainingsdauerStunden: v.trainingsdauerStunden ?? '',
    trainerqualifikation: v.trainerqualifikation ?? '',
    vorgabequelle: v.vorgabequelle ?? '',
    vorgabefassung: v.vorgabefassung ?? '',
  };
}

function leerAlsNull(wert: string): string | null {
  const geputzt = wert.trim();
  return geputzt === '' ? null : geputzt;
}
